import { Component, Input, OnDestroy, OnInit, SimpleChanges, OnChanges } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { debounceTime, distinctUntilChanged, finalize, forkJoin, of, Subject, Subscription } from 'rxjs';
import { ProductionInventoryService } from '../../services/production-inventory.service';
import {
  Factory,
  ProductStockAdjustment,
  StockAdjustmentDirection,
  StockAdjustmentFilters,
  StockAdjustmentReason,
  stockAdjustmentReasonLabel,
  stockAdjustmentReasonOptions,
  StockAdjustmentScope,
} from '../../production-inventory.model';
import { ProductService } from '../../../products/product.service';
import { ProductNameList } from '../../../products/product.model';
import { EmployeesService } from '../../../employees/employees.service';
import { EmployeeBasicInfo } from '../../../employees/employees.model';
import { RawMaterialService } from '../../../raw-material-inventory/raw-material.service';
import { RawMaterialWarehouse } from '../../../raw-material-inventory/raw-material.model';
import { PaginatedResponse } from '../../../common/models/common.model';
import { decimalSign, formatDecimalQuantity, formatSignedDecimalQuantity } from '../../../utils';

const PAGE_SIZE = 10;

@Component({
  selector: 'app-product-stock-adjustments',
  standalone: false,
  templateUrl: './product-stock-adjustments.component.html',
  styleUrl: './product-stock-adjustments.component.scss',
})
export class ProductStockAdjustmentsComponent implements OnInit, OnChanges, OnDestroy {
  /** Which go-down's ledger this is. Drives the endpoint, the reasons and the labels. */
  @Input({ required: true }) scope!: StockAdjustmentScope;

  @Input() factoryList: Factory[] = [];

  /**
   * Set when this is embedded as one batch's history rather than the standalone screen.
   * It pins the list to that batch and hides the filter row, which would only ever
   * narrow a handful of rows.
   */
  @Input() productionEntry: string | null = null;

  /** Pins a warehouse ledger to one warehouse, for a stock row's history. */
  @Input() warehouse: string | null = null;

  /** Bumped by the host after an adjustment is posted, to pull the ledger back in sync. */
  @Input() refreshToken: number = 0;

  protected adjustments: ProductStockAdjustment[] = [];
  protected products: ProductNameList[] = [];
  protected employees: EmployeeBasicInfo[] = [];
  protected warehouses: RawMaterialWarehouse[] = [];

  protected isLoading: boolean = false;
  protected isForbidden: boolean = false;
  protected pageError: any = null;

  protected currentPage: number = 1;
  protected totalCount: number = 0;
  protected hasNextPage: boolean = false;
  protected hasPreviousPage: boolean = false;

  protected searchTerm: string = '';
  protected selectedContainer: string | null = null;
  protected selectedProduct: string | null = null;
  protected selectedReason: StockAdjustmentReason | null = null;
  protected selectedCreatedBy: string | null = null;
  protected selectedDirection: StockAdjustmentDirection | null = null;
  protected fromDate: string | null = null;
  protected toDate: string | null = null;

  protected readonly pageSize = PAGE_SIZE;
  protected readonly directionOptions = [
    { value: StockAdjustmentDirection.ADD, label: 'Added stock' },
    { value: StockAdjustmentDirection.REMOVE, label: 'Removed stock' },
  ];

  private listSubscription!: Subscription;
  private optionsSubscription!: Subscription;
  private readonly searchTerms: Subject<string> = new Subject<string>();

  constructor(
    private productionInventoryService: ProductionInventoryService,
    private productService: ProductService,
    private employeesService: EmployeesService,
    private rawMaterialService: RawMaterialService
  ) {}

  protected get isFactory(): boolean {
    return this.scope === 'factory';
  }

  /** "Factory" or "Warehouse", for the column header and the filter placeholder. */
  protected get containerLabel(): string {
    return this.isFactory ? 'Factory' : 'Warehouse';
  }

  protected get containerOptions(): { id: string; name: string }[] {
    return this.isFactory ? this.factoryList : this.warehouses;
  }

  protected get reasonOptions() {
    return stockAdjustmentReasonOptions(this.scope);
  }

  ngOnInit(): void {
    if (!this.isBatchScoped) {
      this.loadFilterOptions();
    }

    this.fetchAdjustments();

    this.searchTerms.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => {
      this.currentPage = 1;
      this.fetchAdjustments();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['refreshToken'] && !changes['refreshToken'].firstChange) {
      this.currentPage = 1;
      this.fetchAdjustments();
    }
  }

  protected get isBatchScoped(): boolean {
    return !!this.productionEntry;
  }

  protected get hasActiveFilters(): boolean {
    return !!(
      this.searchTerm.trim() ||
      this.selectedContainer ||
      this.selectedProduct ||
      this.selectedReason ||
      this.selectedCreatedBy ||
      this.selectedDirection ||
      this.fromDate ||
      this.toDate
    );
  }

  protected onSearchChange(term: string): void {
    this.searchTerm = term;
    this.searchTerms.next(term.trim());
  }

  protected onFilterChange(): void {
    this.currentPage = 1;
    this.fetchAdjustments();
  }

  protected clearFilters(): void {
    this.searchTerm = '';
    this.selectedContainer = null;
    this.selectedProduct = null;
    this.selectedReason = null;
    this.selectedCreatedBy = null;
    this.selectedDirection = null;
    this.fromDate = null;
    this.toDate = null;

    this.onFilterChange();
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.fetchAdjustments();
  }

  protected isRemoval(adjustment: ProductStockAdjustment): boolean {
    return decimalSign(adjustment.quantity) < 0;
  }

  protected signedQuantity(adjustment: ProductStockAdjustment): string {
    return `${formatSignedDecimalQuantity(adjustment.quantity)} units`;
  }

  /** The server sends boxes already signed, or "-" when the packaging has no nos. */
  protected boxesLabel(adjustment: ProductStockAdjustment): string | null {
    const boxes = (adjustment.boxes ?? '').trim();
    if (!boxes || boxes === '-') return null;

    return `${formatSignedDecimalQuantity(boxes)} boxes`;
  }

  protected batchBalanceAfter(adjustment: ProductStockAdjustment): string {
    return `${formatDecimalQuantity(adjustment.batch_units_available_after)} units`;
  }

  protected containerBalanceAfter(adjustment: ProductStockAdjustment): string {
    return `${formatDecimalQuantity(adjustment.container_units_available_after)} units`;
  }

  protected reasonLabel(adjustment: ProductStockAdjustment): string {
    // Prefer our own wording; fall back to the server's if it ever sends a new code.
    return stockAdjustmentReasonLabel(this.scope, adjustment.reason) || adjustment.reason_display;
  }

  private fetchAdjustments(): void {
    this.pageError = null;
    this.isLoading = true;

    this.listSubscription?.unsubscribe();
    this.listSubscription = this.productionInventoryService
      .getStockAdjustments(this.scope, this.currentFilters)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response: PaginatedResponse<ProductStockAdjustment>) => {
          this.isForbidden = false;
          this.adjustments = response.results ?? [];
          this.totalCount = response.count ?? 0;
          this.hasNextPage = !!response.next;
          this.hasPreviousPage = !!response.previous;
        },
        error: (error: HttpErrorResponse) => {
          this.adjustments = [];
          this.totalCount = 0;

          // A 403 here is the endpoint's admin-only gate, not a fault to retry.
          if (error?.status === 403) {
            this.isForbidden = true;
            return;
          }

          this.pageError = error;
        },
      });
  }

  /** Every relation filter sends a UUID: a name comes back as an empty 200. */
  private get currentFilters(): StockAdjustmentFilters {
    if (this.isBatchScoped) {
      // A batch can sit in several warehouses, so a stock row's history pins both.
      return { page: this.currentPage, production_entry: this.productionEntry, warehouse: this.warehouse };
    }

    const filters: StockAdjustmentFilters = {
      page: this.currentPage,
      search: this.searchTerm.trim() || null,
      product: this.selectedProduct,
      reason: this.selectedReason,
      created_by: this.selectedCreatedBy,
      direction: this.selectedDirection,
      from_date: this.fromDate,
      to_date: this.toDate,
    };

    if (this.isFactory) {
      filters.factory = this.selectedContainer;
    } else {
      filters.warehouse = this.selectedContainer;
    }

    return filters;
  }

  private loadFilterOptions(): void {
    this.optionsSubscription = forkJoin({
      products: this.productService.lighter_list(),
      employees: this.employeesService.lighter_list(),
      warehouses: this.isFactory ? of([] as RawMaterialWarehouse[]) : this.rawMaterialService.get_warehouses(),
    }).subscribe({
      next: ({ products, employees, warehouses }) => {
        this.products = products ?? [];
        this.employees = employees ?? [];
        this.warehouses = warehouses ?? [];
      },
      // Filter options are a convenience; the list itself still works without them.
      error: () => undefined,
    });
  }

  ngOnDestroy(): void {
    this.listSubscription?.unsubscribe();
    this.optionsSubscription?.unsubscribe();
    this.searchTerms.complete();
  }
}
